import { get, useFormContext, FieldPath, FieldValues, ValidationRule } from 'react-hook-form';
import { AutoSuggestAddressEntryMode, AutoSuggestAddressType } from '@onegov/react-component-library';
import { useEffect } from 'react';
import { AddressModel, FormAutoSuggestAddressErrorMessages, ManualAddressErrorKeys, UnverifiedAddress } from './types';
import { useLatest } from 'react-use';

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  name: TName;
  required?: boolean;
  addressType?: AutoSuggestAddressType;
  entryMode: AutoSuggestAddressEntryMode;
  errorMessages?: FormAutoSuggestAddressErrorMessages;
  manualStreetAddressMaxLength?: ValidationRule<number>;
  manualSuburbMaxLength?: ValidationRule<number>;
}

/**
 * Represents the AutoSuggestAddress control running in manual mode. Note that this component actually doesn't
 * need to render anything; it's solely responsible for registering/unregistering the input fields that the
 * underlying control renders in manual mode, so that error messages can be triggered for all of those fields
 * at the same time (instead of only field at a time).
 */
export const AutoSuggestAddressManualFields = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  required,
  entryMode,
  addressType,
  errorMessages,
  manualStreetAddressMaxLength,
  manualSuburbMaxLength,
}: Props<TFieldValues, TName>) => {
  const errorMessagesRef = useLatest(errorMessages ?? {});

  const context = useFormContext<TFieldValues>();
  const { register, unregister } = context;

  // Helper function to build a field name ('fn') compatible with `TName`
  const fn = (suffix: string) => `${name}${suffix}` as TName;

  useEffect(() => {
    if (entryMode !== 'manual') {
      unregister(fn('StreetAddress'));
      unregister(fn('Suburb'));
      unregister(fn('State'));
      unregister(fn('Postcode'));
      return;
    }

    const getErrorMessage = (errorKey: ManualAddressErrorKeys, defaultMessage: string): string =>
      errorMessagesRef.current[errorKey] ?? defaultMessage;

    const buildAddressFieldRule =
      (validate: (manualAddress: UnverifiedAddress) => string | undefined) =>
      // Since each field in manual mode has been registered only to create validators, the first value passed to
      // the validation expression will always be 'undefined' (since the field doesn't really exist). The second
      // parameter passed to validators by RHF is the form state; we can use that to look up the address model
      // based on the address field's name.
      (undef: undefined, formValues: TFieldValues) => {
        const address = get(formValues, name) as AddressModel;
        return Boolean(address?.manualAddress) ? validate(address.manualAddress!) : undefined;
      };

    const buildManualAddressFieldRequiredRule = (
      getFieldValue: (manualAddress: UnverifiedAddress) => string | undefined,
      getErrorMessage: () => string
    ) =>
      buildAddressFieldRule((manualAddress) => {
        if (!required) return undefined;

        const fieldValue = getFieldValue(manualAddress);
        return Boolean(fieldValue) ? undefined : getErrorMessage();
      });

    const buildManualAddressFieldMaxLengthRule = (
      defaultFieldLabel: string,
      getFieldValue: (manualAddress: UnverifiedAddress) => string | undefined,
      maxLengthRule?: ValidationRule<number>
    ) =>
      buildAddressFieldRule((manualAddress) => {
        if (!maxLengthRule) return undefined;
        const fieldValue = getFieldValue(manualAddress);
        const maxLength: number = typeof maxLengthRule === 'number' ? maxLengthRule : maxLengthRule.value ?? 0;
        const maxLengthMessage =
          typeof maxLengthRule === 'number'
            ? `You've exceeded the maximum number of characters (${maxLength}) for ${defaultFieldLabel}.`
            : maxLengthRule.message ?? '';
        return (fieldValue || '').length <= maxLength ? undefined : maxLengthMessage;
      });

    register(fn('StreetAddress'), {
      validate: {
        required: buildManualAddressFieldRequiredRule(
          (manualAddress) => manualAddress.streetAddress,
          () => getErrorMessage('streetAddressRequired', 'Enter the street address.')
        ),

        streetAddress: buildAddressFieldRule((manualAddress) => {
          if (addressType !== 'physical') return undefined;

          return isVirtualAddress(manualAddress.streetAddress)
            ? getErrorMessage('physicalAddress', 'Address cannot be a PO box. Please enter a street address.')
            : undefined;
        }),

        maxLength: buildManualAddressFieldMaxLengthRule(
          'Street address',
          (manualAddress) => manualAddress.streetAddress,
          manualStreetAddressMaxLength
        ),
      },
    });

    register(fn('Suburb'), {
      validate: {
        required: buildManualAddressFieldRequiredRule(
          (manualAddress) => manualAddress.suburb,
          () => getErrorMessage('suburbRequired', 'Enter the suburb.')
        ),
        maxLength: buildManualAddressFieldMaxLengthRule(
          'Suburb',
          (manualAddress) => manualAddress.suburb,
          manualSuburbMaxLength
        ),
      },
    });

    register(fn('State'), {
      validate: {
        required: buildManualAddressFieldRequiredRule(
          (manualAddress) => manualAddress.state,
          () => getErrorMessage('stateRequired', 'Select the state.')
        ),
      },
    });

    register(fn('Postcode'), {
      validate: {
        required: buildManualAddressFieldRequiredRule(
          (manualAddress) => manualAddress.postcode,
          () => getErrorMessage('postcodeRequired', 'Enter the postcode.')
        ),

        valid: buildAddressFieldRule((manualAddress) => {
          const { postcode } = manualAddress;
          return /^\d{4}$/.test(postcode!)
            ? undefined
            : getErrorMessage('postcodeValid', 'Enter a valid postcode (four digits)');
        }),
      },
    });
  }, [entryMode, addressType, required]);

  return null;
};

function isVirtualAddress(streetAddress?: string) {
  return (
    Boolean(streetAddress) &&
    /po\s+box|post\s+office\s+box|locked\s+bag|gpo\s+box|private\s+bag|common\s+box/i.test(streetAddress!)
  );
}
